
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * LegalDocumentUploadResult struct
 */
class LegalDocumentUploadResult {
  /**
   * Initialize struct
   * @param {!number=} documentId
   * @constructor
   */
  constructor (documentId = new UInt64(0, 0)) {
    this.DocumentId = documentId
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LegalDocumentUploadResult}
   * @param {!LegalDocumentUploadResult} other Other struct
   * @returns {!LegalDocumentUploadResult} This struct
   */
  copy (other) {
    if (other.DocumentId != null) {
      this.DocumentId = UInt64.fromNumber(other.DocumentId)
    } else {
      this.DocumentId = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LegalDocumentUploadResult}
   * @returns {!LegalDocumentUploadResult} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LegalDocumentUploadResultModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LegalDocumentUploadResultModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LegalDocumentUploadResult}
   * @param {!LegalDocumentUploadResult} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LegalDocumentUploadResult)) {
      throw new TypeError('Instance of LegalDocumentUploadResult is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LegalDocumentUploadResult}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      DocumentId: ((this.DocumentId != null) ? this.DocumentId.toNumber() : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LegalDocumentUploadResult.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LegalDocumentUploadResult} other Object value
   * @returns {!LegalDocumentUploadResult} Created struct
   */
  static fromObject (other) {
    return new LegalDocumentUploadResult().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LegalDocumentUploadResult}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LegalDocumentUploadResult.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LegalDocumentUploadResult}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 15023
  }
}

export { LegalDocumentUploadResult };

/**
 * Fast Binary Encoding LegalDocumentUploadResult field model
 */
class FieldModelLegalDocumentUploadResult extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.DocumentId = new fbe.FieldModelUInt64(buffer, 4 + 4)
  }

  /**
   * Get the field size
   * @this {!FieldModelLegalDocumentUploadResult}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLegalDocumentUploadResult}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.DocumentId.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLegalDocumentUploadResult}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.DocumentId.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLegalDocumentUploadResult}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLegalDocumentUploadResult.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLegalDocumentUploadResult}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 15023
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.DocumentId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentId.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLegalDocumentUploadResult}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {!LegalDocumentUploadResult} fbeValue Default value, defaults is new LegalDocumentUploadResult()
   * @returns {!LegalDocumentUploadResult} LegalDocumentUploadResult value
   */
  get (fbeValue = new LegalDocumentUploadResult()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {!LegalDocumentUploadResult} fbeValue LegalDocumentUploadResult value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.DocumentId.fbeSize) <= fbeStructSize) {
      fbeValue.DocumentId = this.DocumentId.get()
    } else {
      fbeValue.DocumentId = new UInt64(0, 0)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.DocumentId.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLegalDocumentUploadResult}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {!LegalDocumentUploadResult} fbeValue LegalDocumentUploadResult value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLegalDocumentUploadResult}
   * @param {LegalDocumentUploadResult} fbeValue LegalDocumentUploadResult value
   */
  setFields (fbeValue) {
    this.DocumentId.set(fbeValue.DocumentId)
  }
}

export { FieldModelLegalDocumentUploadResult };

/**
 * Fast Binary Encoding LegalDocumentUploadResult model
 */
class LegalDocumentUploadResultModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLegalDocumentUploadResult(this.buffer, 4)
  }

  /**
   * Get the LegalDocumentUploadResult model
   * @this {!LegalDocumentUploadResultModel}
   * @returns {!FieldModelLegalDocumentUploadResult} model LegalDocumentUploadResult model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LegalDocumentUploadResultModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LegalDocumentUploadResultModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LegalDocumentUploadResultModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LegalDocumentUploadResultModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLegalDocumentUploadResult.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LegalDocumentUploadResultModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LegalDocumentUploadResultModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LegalDocumentUploadResultModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LegalDocumentUploadResultModel}
   * @param {!LegalDocumentUploadResult} value LegalDocumentUploadResult value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LegalDocumentUploadResultModel}
   * @param {!LegalDocumentUploadResult} value LegalDocumentUploadResult value, defaults is new LegalDocumentUploadResult()
   * @return {!object} Deserialized LegalDocumentUploadResult value and its size
   */
  deserialize (value = new LegalDocumentUploadResult()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LegalDocumentUploadResult(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LegalDocumentUploadResult(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LegalDocumentUploadResultModel}
   * @param {!number} prev Previous LegalDocumentUploadResult model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LegalDocumentUploadResultModel };
